const MAX_PER_SESSION = 30;
const FEE_PER_CHILD = 15;
const SURCHARGE_CAP = 40;

function formatMoney(cents, format) {
  const amount = (cents / 100).toFixed(2);
  if (!format) return '$' + amount;
  return format.replace(/\{\{\s*amount\s*\}\}/, amount);
}

function findMatchingSessionVariant(sessionVariants, workshopPriceCents) {
  if (!Array.isArray(sessionVariants)) return null;
  return sessionVariants.find((variant) => variant.price === workshopPriceCents) || null;
}

function computeBooking(count, workshopPriceCents, sessionVariants) {
  if (count <= MAX_PER_SESSION) {
    return {
      tier: 'standard',
      sessions: 1,
      extraChildren: 0,
      baseTotal: workshopPriceCents,
      feeTotal: 0,
      sessionTotal: 0,
      total: workshopPriceCents,
      message: '1 session required. Includes up to 30 children.',
    };
  }

  if (count <= SURCHARGE_CAP) {
    const extraChildren = count - MAX_PER_SESSION;
    const feeTotal = extraChildren * FEE_PER_CHILD * 100;
    return {
      tier: 'surcharge',
      sessions: 1,
      extraChildren,
      baseTotal: workshopPriceCents,
      feeTotal,
      sessionTotal: 0,
      total: workshopPriceCents + feeTotal,
      message:
        '1 session required. Your booking includes 30 children, plus ' +
        extraChildren +
        ' additional children at $15 each.',
    };
  }

  const sessions = Math.ceil(count / MAX_PER_SESSION);
  const extraSessions = sessions - 1;
  const sessionVariant = findMatchingSessionVariant(sessionVariants, workshopPriceCents);
  const sessionTotal = sessionVariant ? sessionVariant.price * extraSessions : 0;

  return {
    tier: 'multi-session',
    sessions,
    extraSessions,
    extraChildren: 0,
    baseTotal: workshopPriceCents,
    feeTotal: 0,
    sessionTotal,
    total: workshopPriceCents + sessionTotal,
    sessionVariantId: sessionVariant ? sessionVariant.id : null,
    requiresDateTime: true,
    message:
      sessions +
      ' sessions are required for ' +
      count +
      ' children. We can accommodate up to 30 children per session and will contact you after booking to confirm the timing of all sessions.',
  };
}

function generateBookingReference() {
  return 'BK-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).slice(2, 6).toUpperCase();
}

function initWorkshopCalculator(root) {
  const input = root.querySelector('[data-workshop-children-input]');
  const errorEl = root.querySelector('[data-workshop-error]');
  const datetimeField = root.querySelector('[data-workshop-datetime-field]');
  const dateInput = root.querySelector('[data-workshop-preferred-date]');
  const timeInput = root.querySelector('[data-workshop-preferred-time]');
  const summaryEl = root.querySelector('[data-workshop-summary]');
  const summaryMessageEl = root.querySelector('[data-workshop-summary-message]');
  const summaryBaseEl = root.querySelector('[data-workshop-summary-base]');
  const summaryFeeLineEl = root.querySelector('[data-workshop-summary-fee-line]');
  const summaryFeeEl = root.querySelector('[data-workshop-summary-fee]');
  const summarySessionLineEl = root.querySelector('[data-workshop-summary-session-line]');
  const summarySessionEl = root.querySelector('[data-workshop-summary-session]');
  const summaryTotalEl = root.querySelector('[data-workshop-summary-total]');
  const addButton = root.querySelector('[data-workshop-add-button]');

  if (!input || !addButton) return;

  const workshopVariantId = root.getAttribute('data-workshop-variant-id');
  const workshopPrice = parseInt(root.getAttribute('data-workshop-price'), 10) || 0;
  const feeVariantId = root.getAttribute('data-fee-variant-id');
  const programName = root.getAttribute('data-program-name') || '';
  const moneyFormat = root.getAttribute('data-money-format');

  let sessionVariants = [];
  try {
    sessionVariants = JSON.parse(root.getAttribute('data-session-variants') || '[]');
  } catch (error) {
    sessionVariants = [];
  }

  let currentBooking = null;

  function setError(message) {
    if (!errorEl) return;
    if (message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    } else {
      errorEl.textContent = '';
      errorEl.hidden = true;
      input.removeAttribute('aria-invalid');
    }
  }

  function hasValidDateTime() {
    return Boolean(dateInput && timeInput && dateInput.value && timeInput.value);
  }

  function resetToEmpty() {
    setError('');
    summaryEl.hidden = true;
    datetimeField.hidden = true;
    addButton.hidden = false;
    addButton.disabled = true;
    currentBooking = null;
  }

  function render() {
    const raw = input.value.trim();

    if (raw === '') {
      resetToEmpty();
      return;
    }

    const count = Number(raw);

    if (!Number.isInteger(count) || count < 1) {
      setError('Please enter a whole number of at least 1.');
      summaryEl.hidden = true;
      datetimeField.hidden = true;
      addButton.hidden = false;
      addButton.disabled = true;
      currentBooking = null;
      return;
    }

    setError('');
    const booking = computeBooking(count, workshopPrice, sessionVariants);
    currentBooking = { count, ...booking };

    summaryEl.hidden = false;
    summaryMessageEl.textContent = booking.message;
    summaryBaseEl.closest('.workshop-calculator__summary-line').hidden = false;
    summaryTotalEl.closest('.workshop-calculator__summary-line').hidden = false;
    summaryBaseEl.textContent = formatMoney(booking.baseTotal, moneyFormat);

    if (booking.feeTotal > 0) {
      summaryFeeLineEl.hidden = false;
      summaryFeeEl.textContent = formatMoney(booking.feeTotal, moneyFormat);
    } else {
      summaryFeeLineEl.hidden = true;
    }

    if (booking.tier === 'multi-session') {
      datetimeField.hidden = false;

      if (!booking.sessionVariantId) {
        summarySessionLineEl.hidden = true;
        setError(
          'This program is not yet set up for group bookings of this size. Please contact us to arrange your booking.'
        );
        addButton.disabled = true;
        addButton.hidden = false;
        return;
      }

      summarySessionLineEl.hidden = false;
      summarySessionEl.textContent = formatMoney(booking.sessionTotal, moneyFormat);
    } else {
      datetimeField.hidden = true;
      summarySessionLineEl.hidden = true;
    }

    summaryTotalEl.textContent = formatMoney(booking.total, moneyFormat);
    addButton.hidden = false;

    if (booking.tier === 'multi-session' && !hasValidDateTime()) {
      addButton.disabled = true;
    } else {
      addButton.disabled = false;
    }
  }

  input.addEventListener('input', render);
  if (dateInput) dateInput.addEventListener('change', render);
  if (timeInput) timeInput.addEventListener('change', render);

  addButton.addEventListener('click', async function () {
    if (!currentBooking) return;
    if (currentBooking.tier === 'multi-session' && (!hasValidDateTime() || !currentBooking.sessionVariantId)) return;

    addButton.disabled = true;
    addButton.textContent = 'Adding…';

    const bookingReference = generateBookingReference();
    const items = [];

    if (currentBooking.tier === 'standard') {
      items.push({
        id: workshopVariantId,
        quantity: 1,
        properties: {
          'Number of children': String(currentBooking.count),
          'Sessions required': '1 session',
          'Additional children': '0',
          'Program name': programName,
          '_Booking reference': bookingReference,
        },
      });
    } else if (currentBooking.tier === 'surcharge') {
      items.push({
        id: workshopVariantId,
        quantity: 1,
        properties: {
          'Number of children': String(currentBooking.count),
          'Sessions required': '1 session',
          'Additional children': String(currentBooking.extraChildren),
          'Program name': programName,
          '_Booking reference': bookingReference,
        },
      });

      if (feeVariantId) {
        items.push({
          id: feeVariantId,
          quantity: currentBooking.extraChildren,
          properties: {
            'Program name': programName,
            For: 'Additional children beyond 30 for ' + programName,
            '_Booking reference': bookingReference,
          },
        });
      }
    } else if (currentBooking.tier === 'multi-session') {
      const preferredDateTime = dateInput.value + ' ' + timeInput.value;

      items.push({
        id: workshopVariantId,
        quantity: 1,
        properties: {
          'Number of children': String(currentBooking.count),
          'Sessions required': currentBooking.sessions + ' sessions',
          'Preferred date and time': preferredDateTime,
          'Program name': programName,
          '_Booking reference': bookingReference,
        },
      });

      items.push({
        id: currentBooking.sessionVariantId,
        quantity: currentBooking.extraSessions,
        properties: {
          'Program name': programName,
          For: currentBooking.extraSessions + ' additional session(s) for ' + programName,
          '_Booking reference': bookingReference,
        },
      });
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Something went wrong adding this booking to your cart.');
      }

      window.location.href = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
        ? window.Shopify.routes.root + 'cart'
        : '/cart';
    } catch (error) {
      setError(error.message || 'Something went wrong adding this booking to your cart. Please try again.');
      addButton.disabled = false;
      addButton.textContent = 'Add to Cart';
    }
  });

  render();
}

document.querySelectorAll('[data-workshop-calculator]').forEach(initWorkshopCalculator);
